import { labelWeight, resolveColor, type ButtonElement, type Element, type FrameElement, type Theme } from "./vocabulary.ts";

/**
 * The flex mapping, the same contract as render_html.py: column and row are flex directions, hug is
 * fit-content, fill is flex: 1 on the main axis and align-self: stretch on the cross axis. Returned
 * as ordered declarations so the golden test can compare strings with the Python output.
 */

export const PLACEHOLDER_FILL = "#E5E7EB";
export const BUTTON = { padding: [12, 20], radius: 8, size: 16, white: "#FFFFFF" } as const;
const ALIGN_CSS = { start: "flex-start", center: "center", end: "flex-end" } as const;
const JUSTIFY_CSS = { start: "flex-start", center: "center", end: "flex-end", between: "space-between" } as const;

export type Declarations = [string, string][];

export interface ButtonStyle {
	fill: string | null;
	stroke: string | null;
	strokeWidth: number;
	textColor: string;
}

export function buttonStyle(theme: Theme, variant: ButtonElement["variant"]): ButtonStyle {
	const accent = resolveColor(theme, "accent");
	if (variant === "primary") return { fill: accent, stroke: null, strokeWidth: 0, textColor: BUTTON.white };
	if (variant === "secondary") return { fill: null, stroke: accent, strokeWidth: 1, textColor: accent };
	return { fill: null, stroke: null, strokeWidth: 0, textColor: accent };
}

export function declarations(node: Element, parent: FrameElement | null, theme: Theme): Declarations {
	switch (node.kind) {
		case "frame":
			return frameDeclarations(node, parent, theme, {
				fill: node.fill,
				stroke: node.stroke,
				strokeWidth: node.strokeWidth ?? 1,
				radius: node.radius,
				padding: node.padding ?? 0,
				layout: node.layout,
				align: node.align ?? "start",
				justify: node.justify ?? "start",
				gap: node.gap ?? 0,
			});
		case "text": {
			const out: Declarations = [
				["font-size", px(node.size)],
				["font-weight", String(node.weight)],
				["color", resolveColor(theme, node.color)],
				["white-space", "pre-wrap"],
			];
			if (node.lineHeight !== undefined) out.push(["line-height", px(node.lineHeight)]);
			if (node.align !== undefined) out.push(["text-align", node.align]);
			return [...out, ...sizeDeclarations(node, parent)];
		}
		case "image": {
			const out: Declarations = [["width", px(node.width)], ["height", px(node.height)], ["flex", "none"]];
			if (node.radius) out.push(["border-radius", px(node.radius)]);
			if (node.src === "placeholder") out.push(["background", PLACEHOLDER_FILL]);
			else out.push(["object-fit", node.fit ?? "cover"]);
			return out;
		}
		case "button": {
			const variant = buttonStyle(theme, node.variant);
			return frameDeclarations(node, parent, theme, {
				fill: variant.fill ?? undefined,
				stroke: variant.stroke ?? undefined,
				strokeWidth: variant.strokeWidth,
				radius: BUTTON.radius,
				padding: [...BUTTON.padding],
				layout: "row",
				align: "center",
				justify: "center",
				gap: 0,
			});
		}
	}
}

/** The label span inside a button. */
export function labelDeclarations(node: ButtonElement, theme: Theme): Declarations {
	return [
		["font-size", px(BUTTON.size)],
		["font-weight", String(labelWeight(theme))],
		["color", buttonStyle(theme, node.variant).textColor],
	];
}

/** Declarations as a React style object; hyphenated names are fine for inline styles. */
export function styleFor(node: Element, parent: FrameElement | null, theme: Theme): Record<string, string> {
	return Object.fromEntries(declarations(node, parent, theme));
}

interface FrameLook {
	fill?: string;
	stroke?: string;
	strokeWidth: number;
	radius?: number;
	padding: number | number[];
	layout: "column" | "row";
	align: keyof typeof ALIGN_CSS;
	justify: keyof typeof JUSTIFY_CSS;
	gap: number;
}

function frameDeclarations(node: Element, parent: FrameElement | null, theme: Theme, look: FrameLook): Declarations {
	const out: Declarations = [
		["display", "flex"],
		["flex-direction", look.layout],
		["gap", px(look.gap)],
		["padding", paddingCss(look.padding)],
		["align-items", ALIGN_CSS[look.align]],
		["justify-content", JUSTIFY_CSS[look.justify]],
		...sizeDeclarations(node, parent),
	];
	if (look.fill) out.push(["background", resolveColor(theme, look.fill)]);
	if (look.stroke) out.push(["border", `${px(look.strokeWidth)} solid ${resolveColor(theme, look.stroke)}`]);
	if (look.radius) out.push(["border-radius", px(look.radius)]);
	return out;
}

/** width and height as CSS, each depending on whether it lies along the parent's main axis. */
function sizeDeclarations(node: Element, parent: FrameElement | null): Declarations {
	const parentLayout = parent ? parent.layout : "column";
	const out: Declarations = [];
	for (const prop of ["width", "height"] as const) {
		const value = (node as { width?: unknown; height?: unknown })[prop] ?? "hug";
		const mainAxis = (prop === "width") === (parentLayout === "row");
		if (value === "hug") out.push([prop, "fit-content"]);
		else if (value === "fill") out.push(mainAxis ? ["flex", "1 1 0"] : ["align-self", "stretch"]);
		else {
			out.push([prop, px(value as number)]);
			if (mainAxis) out.push(["flex", "none"]);
		}
	}
	return out;
}

function paddingCss(value: number | number[]): string {
	return (Array.isArray(value) ? value : [value]).map(px).join(" ");
}

function px(value: number): string {
	return `${value}px`;
}
